import {
	IonBackButton,
	IonButton,
	IonButtons,
	IonContent,
	IonFooter,
	IonIcon,
	IonPage,
	IonToolbar,
	useIonToast,
	useIonViewWillEnter,
	useIonViewWillLeave,
} from "@ionic/react";
import classNames from "classnames";
import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import { BottomSheet, BottomSheetRef } from "react-spring-bottom-sheet";
import InfiniteText from "../components/InfiniteText";
import MediaPlayer from "../components/MediaPlayer/MediaPlayer";
import useStop from "../hooks/useStop";
import backBtnIcon from "../theme/icons/arrow-left.svg";
import closeIcon from "../theme/icons/close.svg";
import likeIcon from "../theme/icons/like.svg";
import likedIcon from "../theme/icons/liked.svg";
import styles from "./Stop.module.css";

/**
 * Page where user can list/watch the stop's media content and read more about the stop
 */
const Stop: React.FC = () => {
	const { t } = useTranslation();
	const { stopNumber } = useParams<{ stopNumber: string }>();
	const { stop, toggleLike } = useStop(stopNumber);
	const bottomSheetRef = useRef<BottomSheetRef>(null);
	const [isBottomHidden, setIsBottomHidden] = useState(true);
	const [isLikeButtonDisabled, setIsLikeButtonDisabled] = useState(false);
	const [presentToast, dismissToast] = useIonToast();

	const playPauseWrapperRef = useRef<HTMLDivElement>(null);
	const seekBarWrapperRef = useRef<HTMLDivElement>(null);
	const playHeadWrapperRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (!stop) {
			return;
		}

		document.title = stop.title;
	}, [stop]);

	// Collapse the bottom sheet on view leave
	// and hide the bottom elements
	useIonViewWillLeave(() => {
		if (!bottomSheetRef.current) {
			return;
		}

		bottomSheetRef.current.snapTo(({ snapPoints }) =>
			Math.min(...snapPoints)
		);
		setIsBottomHidden(true);
	});

	// Show bottom elements on view enter
	useIonViewWillEnter(() => {
		if (!bottomSheetRef.current) {
			return;
		}

		setIsBottomHidden(false);
	});

	/**
	 * Toggles like on the stop if a user has connection
	 * otherwise shows the error message explaining that a connection
	 * is required to toggle like.
	 */
	const handleLikeButtonClick = async () => {
		// Disable button while the request is in progress
		setIsLikeButtonDisabled(true);
		// Show message about this functionality is
		// only available online
		if (!navigator.onLine) {
			presentToast({
				position: "top",
				color: "danger",
				buttons: [{ icon: closeIcon, handler: () => dismissToast() }],
				message: t("pages.stop.like_connection_required_error_message"),
			});
			setIsLikeButtonDisabled(false);
			return;
		}

		const isRequestSucceeded = await toggleLike();

		// Notify a user about failed request
		if (!isRequestSucceeded) {
			presentToast({
				position: "top",
				color: "danger",
				buttons: [{ icon: closeIcon, handler: () => dismissToast() }],
				message: t("common.http_error"),
			});
		}

		// Enable button after the request is finished
		setIsLikeButtonDisabled(false);
	};

	return (
		<IonPage>
			<IonContent
				fullscreen={false}
				scrollY={false}
				style={{
					"--background": `url(${stop?.cover.url})`,
				}}
				className={classNames({
					[styles.content]: true,
					[styles.hasVideo]: stop?.media_file.type === "video",
				})}
			>
				<IonToolbar className={styles.topToolbar} slot="fixed">
					<IonButtons slot="start">
						<IonBackButton
							aria-label={t("common.buttons.back")}
							data-cy="back-btn"
							defaultHref="/tabs/tours"
							text=""
							icon={backBtnIcon}
						/>
					</IonButtons>
					<IonButtons slot="end">
						<IonButton
							className={classNames({
								[styles.likeButton]: true,
								[styles.isLiked]: stop?.like?.token.length,
							})}
							onClick={handleLikeButtonClick}
							disabled={isLikeButtonDisabled}
							aria-label={
								stop?.like.token.length
									? t("pages.stop.like_button_remove_like")
									: t("pages.stop.like_button_add_like")
							}
						>
							<IonIcon
								className="is-small"
								slot="icon-only"
								icon={likeIcon}
							></IonIcon>
							<IonIcon
								className={styles.likedIcon}
								slot="icon-only"
								icon={likedIcon}
							></IonIcon>
						</IonButton>
					</IonButtons>
				</IonToolbar>
				<MediaPlayer
					url={stop?.media_file?.url ?? ""}
					playPauseButtonNodeContainer={playPauseWrapperRef.current}
					seekBarNodeContainer={seekBarWrapperRef.current}
					playHeadPositionNodeContainer={playHeadWrapperRef.current}
				/>
				<BottomSheet
					className={classNames({
						[styles.bottomSheet]: true,
						[styles.isHidden]: isBottomHidden,
					})}
					ref={bottomSheetRef}
					// don't trap keyboard's focus because this component is always open
					blocking={false}
					scrollLocking={true}
					// keep always open
					// so the title, the artists name and the media player controls are visible
					open={true}
					snapPoints={({ headerHeight, minHeight }) => {
						return [
							// Min height of the bottom sheet
							// when only the header and the footer are visible
							headerHeight,
							// Expanded snap point where the content section is visible
							// Math.min limits height to 460
							// in case minHeight - headerHeight - footerHeight is less then 460px then apply it
							// to prevent that the footer is pushed to top because there is not enough content to feel 460px height
							headerHeight +
								Math.min(460, minHeight - headerHeight),
						];
					}}
					defaultSnap={({ lastSnap, snapPoints }) =>
						Math.min(...snapPoints)
					}
					header={
						<div className={styles.bottomSheetHeaderContent}>
							<h1 className={styles.title}>
								<InfiniteText text={stop?.title || ""} />
							</h1>
							<p className={styles.artistName}>
								<InfiniteText text={stop?.nome_artista || ""} />
							</p>
						</div>
					}
				>
					<div
						className={classNames({
							[styles.bottomSheetScrollContent]: true,
							[styles.hasNoContent]: !(
								stop?.descrizione || ""
							).trim().length,
						})}
					>
						<p className={styles.description}>
							{stop?.descrizione}
						</p>
					</div>
				</BottomSheet>
				{/* Render footer on the same level as bottom sheet */}
				{/* to be able use the same z index context */}
				{ReactDOM.createPortal(
					<IonFooter
						className={classNames({
							[styles.footer]: true,
							[styles.isHidden]: isBottomHidden,
						})}
					>
						<IonToolbar>
							<div className={styles.mediaPlayerControls}>
								<div ref={playPauseWrapperRef} />
								<div ref={seekBarWrapperRef} />
								<div
									className={styles.playHeadPositionWrapper}
									ref={playHeadWrapperRef}
								/>
							</div>
						</IonToolbar>
					</IonFooter>,
					document.body
				)}
			</IonContent>
		</IonPage>
	);
};

export default Stop;
